export type ScoringQuality = 'perfect' | 'good' | 'ok' | 'bad';
export type Grade = 'SS' | 'S' | 'A' | 'B' | 'C' | 'D';

export const SCORING_V2 = {
  BASE: 100,
  WHIFF_BASE: 50,
  S0: 150,
  ALPHA: 0.5,
  GAMMA: 1.8,
  qualityWeight: {
    perfect: 1.00,
    good: 0.70,
    ok: 0.35,
    bad: 0.00,
  } satisfies Record<ScoringQuality, number>,
  precisionBonusMax: {
    perfect: 25,
    good: 10,
    ok: 0,
    bad: 0,
  } satisfies Record<ScoringQuality, number>,
  accWeight: {
    perfect: 1.00,
    good: 0.80,
    ok: 0.50,
    bad: 0.00,
  } satisfies Record<ScoringQuality, number>,
  gradeThresholds: [
    { grade: 'SS', min: 97.0 },
    { grade: 'S', min: 93.0 },
    { grade: 'A', min: 88.0 },
    { grade: 'B', min: 75.0 },
    { grade: 'C', min: 65.0 },
    { grade: 'D', min: -Infinity },
  ] as const satisfies ReadonlyArray<{ grade: Grade; min: number }>,
} as const;

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}

export function roundTo(value: number, decimals: number): number {
  if (!Number.isFinite(value)) return 0;
  const f = 10 ** Math.max(0, Math.floor(decimals));
  return Math.round(value * f) / f;
}

export function diffMult(speedPxPerSec: number): number {
  if (!Number.isFinite(speedPxPerSec) || speedPxPerSec <= 0) return 1;
  return (speedPxPerSec / SCORING_V2.S0) ** SCORING_V2.ALPHA;
}

export function precisionFromOffsetPx(offsetPx: number, okWindowPx: number): number {
  if (!Number.isFinite(okWindowPx) || okWindowPx <= 0) return 0;
  const t = clamp01(1 - (Math.abs(offsetPx) / okWindowPx));
  return t ** SCORING_V2.GAMMA;
}

export function gradeFromAccuracyPct(accuracyPct: number): Grade {
  const acc = Number.isFinite(accuracyPct) ? accuracyPct : 0;
  for (const threshold of SCORING_V2.gradeThresholds) {
    if (acc >= threshold.min) return threshold.grade;
  }
  return 'D';
}

